import React, { useState } from 'react';
import { PhoneIcon } from '@/components/icons';
import { clickToCallApi } from '@/api';
import { Toast } from './Toast';
import { useRole } from '@/contexts/RoleContext';
import styles from './CallAgentButton.module.css';

interface CallAgentButtonProps {
    agentName?: string;
    agentNumber?: string | number;
}

// ponytail: self-contained so it works inside module-level column render configs
// (no component state threading). Verifier's own agent_id comes from localStorage;
// the target agent's number goes in customer_number, per the click_to_call API.
export const CallAgentButton: React.FC<CallAgentButtonProps> = ({
    agentName,
    agentNumber,
}) => {
    const { selectedRole } = useRole();
    const [calling, setCalling] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '' });

    // ponytail: only document verifiers and the selfOnboard admin can call agents
    const loggedInAgentId =
        localStorage.getItem('agent_id') || localStorage.getItem('agentId');
    const allowed =
        selectedRole?.role_type === 'DOCUMENT_VERIFIER' ||
        loggedInAgentId === 'selfOnboard';
    const cleanNumber = String(agentNumber ?? '').replace(/\D/g, '').slice(-10);
    const canCall = allowed && cleanNumber.length === 10;

    const handleCall = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const verifierId =
            localStorage.getItem('agent_id') || localStorage.getItem('agentId');
        if (!verifierId) {
            setToast({ show: true, message: 'Agent ID not found. Please log in again.' });
            return;
        }
        if (!canCall) {
            setToast({ show: true, message: 'Agent phone number not available' });
            return;
        }
        setCalling(true);
        try {
            const response = await clickToCallApi.clickToCall({
                agent_id: verifierId,
                customer_number: '91' + cleanNumber,
                cp_team: 'sales_team',
                call_type: 'phone',
            });
            setToast({ show: true, message: response.message || 'Call initiated successfully' });
        } catch {
            setToast({ show: true, message: 'Failed to initiate call' });
        } finally {
            setCalling(false);
        }
    };

    return (
        <span className={styles.container}>
            <span className={styles.name}>{agentName || '-'}</span>
            {canCall && (
                <button
                    className={styles.callButton}
                    onClick={handleCall}
                    disabled={calling}
                    aria-label={`Call agent ${agentName || ''}`}
                    title={calling ? 'Calling...' : `Call ${agentName || 'agent'}`}
                >
                    <PhoneIcon size={14} />
                    <span>{calling ? 'Calling...' : 'Call'}</span>
                </button>
            )}
            <Toast
                show={toast.show}
                message={toast.message}
                onClose={() => setToast({ show: false, message: '' })}
            />
        </span>
    );
};
